import { Breadcrumbs, Button, Typography } from '@mui/material'
import { SessionInfo } from '../StudioHome'
import { useNavigate } from 'react-router-dom'

/**
 * Represents a summary of information.
 *
 * @typedef {Object} Summary
 * @property {SessionInfo} [organisation] - An optional organization.
 * @property {SessionInfo} [session] - An optional session.
 * @property {string} [file] - An optional file name.
 */
export type Summary = {
  organisation?: SessionInfo
  session?: SessionInfo
  file?: string
}

/**
 * Limit the size of a string and add an ellipsis (...) if it exceeds a certain length.
 *
 * @param {string} s - The input string.
 * @param {number} limit - The maximum length allowed for the string.
 * @returns {string} The limited string with an ellipsis if necessary.
 */
function limitCrumbSize(s: string) {
  const limit = 20
  if (s.length <= limit) {
    return s
  }
  const trimBy = s.length - limit - 3
  return '...' + s.slice(trimBy)
}

/**
 * Render breadcrumbs indicating the path of pages, such as user_name > session_name > file_name
 * or user_name > organisation_name > session_name > file_name.
 *
 * @component
 * @param {object} props - The component props.
 * @param {Summary} props.summary - An object containing summary information.
 * @returns {JSX.Element} The rendered breadcrumb component.
 */
export default function BreadCrumbs({ summary }: { summary: Summary }): JSX.Element {
  type chainLink = {
    name: string
    link: string | null
  }
  const chain: chainLink[] = []
  if (summary.organisation) {
    chain.push({
      name: summary.organisation.name,
      link: `/app/view/o/${summary.organisation.id}`,
    })
  }
  if (summary.session) {
    chain.push({
      name: summary.session.name,
      link: `/app/view/s/${summary.session.id}`,
    })
  }
  if (summary.file) {
    chain.push({
      name: decodeURIComponent(summary.file).split('/').slice(-1)[0],
      link: null,
    })
  }
  if (chain.length > 0) {
    chain.unshift({ name: 'studio', link: '/app/view/user' })
  }

  const navigate = useNavigate()

  return (
    <Breadcrumbs>
      {chain &&
        chain.map((link, idx) => (
          <div key={idx}>
            {link.link ? (
              <Button variant="text" onClick={() => navigate(link.link ?? '/app/view/user')} title={link.name}>
                {limitCrumbSize(link.name)}
              </Button>
            ) : (
              <Typography color="text.primary" title={link.name} style={{ textDecoration: 'none' }}>
                {limitCrumbSize(link.name)}
              </Typography>
            )}
          </div>
        ))}
    </Breadcrumbs>
  )
}
